import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonGroup, Button } from 'reactstrap';
import { RootState, AppDispatch } from 'src/slices/store';
import {
  setScreen,
  setSelectedMetricKey,
  setDataBy,
  setChartType,
  CompareChartType,
} from 'src/slices/Compare/reducer';
import { fetchCompareGraphData } from 'src/slices/Compare/thunk';
import { COMPARE_METRICS, daysBetween } from './compareUtils';
import DataByDropdown, { OptionType } from 'src/components/Common/DataByDropdown';
import CompareKpiCard from './CompareKpiCard';
import CompareChart from './CompareChart';
import TableCustomLoader from 'src/components/Common/TableCustomLoader';

interface Props {
  viewMode: 'sidebar' | 'fullpage';
  onCollapseViewMode: () => void;
}

const CompareResultsScreen: React.FC<Props> = ({ viewMode, onCollapseViewMode }) => {
  const dispatch = useDispatch<AppDispatch>();

  const openExchange = useSelector((state: RootState) => state.compare.openExchange);
  const exState = useSelector((state: RootState) =>
    openExchange ? state.compare.exchanges[openExchange] : null
  );

  // Lazy-render all-metrics grid after featured chart mounts
  const [showAllMetrics, setShowAllMetrics] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowAllMetrics(true), 50);
    return () => clearTimeout(t);
  }, []);

  // Show loader during expand/collapse transition so charts don't render blank
  const [chartTransitioning, setChartTransitioning] = useState(false);
  const prevViewMode = useRef(viewMode);
  useEffect(() => {
    if (prevViewMode.current !== viewMode) {
      prevViewMode.current = viewMode;
      setChartTransitioning(true);
      // Allow the container CSS transition to finish before remounting charts
      const t = setTimeout(() => setChartTransitioning(false), 400);
      return () => clearTimeout(t);
    }
  }, [viewMode]);

  if (!exState?.widgetData || !exState?.graphData) {
    return (
      <div className="compare-no-data">
        No comparison data available for the selected date ranges.
      </div>
    );
  }

  const exchangeName     = openExchange!; // non-null: guarded by early return above (exState check implies openExchange is set)
  const widgetData       = exState.widgetData;
  const graphData        = exState.graphData;
  const selectedMetrics  = exState.selectedMetrics;
  const selectedMetricKey = exState.selectedMetricKey;
  const dateRange1       = exState.dateRange1;
  const dateRange2       = exState.dateRange2;
  const lineItemIds      = exState.lineItemIds;
  const dataBy           = exState.dataBy;
  const chartType        = exState.chartType;
  const graphLoading     = exState.graphLoading;

  const isFullpage = viewMode === 'fullpage';

  // Use the longer range for DataByDropdown options.
  const days1 = daysBetween(dateRange1.start, dateRange1.end);
  const days2 = daysBetween(dateRange2.start, dateRange2.end);
  const longerRange = days1 >= days2 ? dateRange1 : dateRange2;

  const handleDataByChange = (option: OptionType | null) => {
    if (!option) return;
    const newDataBy = option.value as typeof dataBy;
    dispatch(setDataBy({ exchangeName, dataBy: newDataBy }));
    dispatch(fetchCompareGraphData({
      exchangeName,
      lineItemIds,
      range1Start: dateRange1.start,
      range1End:   dateRange1.end,
      range2Start: dateRange2.start,
      range2End:   dateRange2.end,
      selectedMetrics,
      dataBy: newDataBy,
    }));
  };

  const handleChartTypeChange = (type: CompareChartType) => {
    dispatch(setChartType({ exchangeName, chartType: type }));
  };

  // Always render in the canonical COMPARE_METRICS order, regardless of selection order.
  const orderedMetrics = COMPARE_METRICS.filter(m => selectedMetrics.includes(m.key)).map(m => m.key);

  // Metrics with at least one non-zero value across both ranges — used for charts
  const nonZeroMetrics = orderedMetrics.filter((key) => {
    const r1 = widgetData.range1[key] ?? 0;
    const r2 = widgetData.range2[key] ?? 0;
    return r1 !== 0 || r2 !== 0;
  });

  const allMetricsZero = nonZeroMetrics.length === 0;

  const activeMetric = selectedMetricKey && nonZeroMetrics.includes(selectedMetricKey)
    ? selectedMetricKey
    : nonZeroMetrics[0] ?? null;

  return (
    <div>
      {/* Header row */}
      <div className="compare-results-header">
        <div className="compare-results-title fw-medium">KPI Summary</div>
        <button
          type="button"
          className="btn btn-outline-primary button-secondary"
          onClick={() => {
            if (viewMode === 'fullpage') onCollapseViewMode();
            dispatch(setScreen({ exchangeName, screen: 'selection' }));
          }}
        >
          Edit Comparison
        </button>
      </div>

      {/* KPI cards */}
      <div className={isFullpage ? 'compare-kpi-fullpage-grid' : 'compare-kpi-sidebar-grid'}>
        {orderedMetrics.map((key) => {
          const metricDef = COMPARE_METRICS.find(m => m.key === key);
          if (!metricDef) return null;
          return (
            <CompareKpiCard
              key={key}
              metric={metricDef}
              range1Value={widgetData.range1[key] ?? 0}
              range2Value={widgetData.range2[key] ?? 0}
              percentChange={widgetData.percent_change[key] ?? null}
              isSelected={activeMetric === key}
              onClick={() => dispatch(setSelectedMetricKey({ exchangeName, key }))}
            />
          );
        })}
      </div>

      {/* Charts section — hidden when all selected metrics have zero values in both ranges */}
      {allMetricsZero && (
        <div className="compare-no-data">
          No data available for comparison for all the selected metrics
        </div>
      )}
      {!allMetricsZero && (
        <>
          {/* Controls row: Data By (left) + Chart Type (right) */}
          <div className="compare-controls-row">
            <div className="d-flex align-items-center gap-2">
              <DataByDropdown
                startDate={longerRange.start}
                endDate={longerRange.end}
                selectedOption={dataBy}
                onChange={handleDataByChange}
              />
              {graphLoading && (
                <span className="spinner-border spinner-border-sm text-secondary" role="status" />
              )}
            </div>

            <div className="d-flex align-items-center gap-2">
              <ButtonGroup size="sm">
                <Button
                  color={chartType === 'line' ? 'primary' : 'light'}
                  onClick={() => handleChartTypeChange('line')}
                >
                  Line
                </Button>
                <Button
                  color={chartType === 'bar' ? 'primary' : 'light'}
                  onClick={() => handleChartTypeChange('bar')}
                >
                  Bar
                </Button>
              </ButtonGroup>
            </div>
          </div>

          {/* Featured chart + All metrics — show loader during expand/collapse transition */}
          {chartTransitioning ? (
            <TableCustomLoader length={3} message="Resizing charts" />
          ) : (
            <>
              {/* Featured chart */}
              {activeMetric && (
                <div className={`compare-featured-chart${graphLoading ? ' compare-chart-loading' : ''}`}>
                  {graphData.range1.length === 0 && graphData.range2.length === 0 ? (
                    <div className="compare-no-data">No data available</div>
                  ) : (
                    <CompareChart
                      key={`compare-featured-${activeMetric}-${dataBy}-${chartType}-${viewMode}`}
                      metricKey={activeMetric}
                      range1Data={graphData.range1}
                      range2Data={graphData.range2}
                      dataBy={dataBy}
                      chartType={chartType}
                      variant="featured"
                    />
                  )}
                </div>
              )}

              {/* All metrics grid — only non-zero metrics, lazy rendered */}
              {showAllMetrics && (graphData.range1.length > 0 || graphData.range2.length > 0) && (
                <>
                  <div className="compare-all-metrics-title fw-medium text-muted">All Metrics</div>
                  <div className="compare-charts-grid">
                    {nonZeroMetrics.map((key) => (
                      <CompareChart
                        key={`compare-mini-${key}-${dataBy}-${chartType}-${viewMode}`}
                        metricKey={key}
                        range1Data={graphData.range1}
                        range2Data={graphData.range2}
                        dataBy={dataBy}
                        chartType={chartType}
                        variant="mini"
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

    </div>
  );
};

export default CompareResultsScreen;
